import { Link } from 'react-router-dom';
import { useLanguage } from '../store/LanguageContext';
import { BRAND } from '../config/brand';
import { useAuth } from '../store/AuthContext';
import { Mail, Phone, MapPin, LogIn, Settings, User } from 'lucide-react';

export default function Footer() {
  const { t } = useLanguage();
  const { user } = useAuth();

  return (
    <footer className="bg-[#1a1a1a] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-7">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-9 h-9 bg-[#c47a3a] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">G</span>
              </div>
              <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>
                Gevelsteenbedrijf
              </span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">{t('footer.desc')}</p>
            <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-white/5 rounded-md text-xs text-gray-400">
              <span className="font-semibold text-[#c47a3a]">BE</span> {t('footer.belgian')}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase mb-4 text-[#c47a3a]">{t('footer.nav')}</h4>
            <ul className="space-y-2.5 text-sm text-gray-400">
              {/* v3.2.101 — Footer-navigatie nu identiek aan Navbar:
                   Home / Inspiratie / Custom / Restpartijen / Contact.
                   'Producten' en 'Calculator' verwijderd want ze bestaan
                   niet als aparte pagina (zijn deel van homepage). 'Offerte'
                   verwijderd uit publieke nav (alleen reachable via
                   'Offerte aanvragen'-knop in calculator). */}
              {[
                ['/',              t('nav.home')],
                ['/inspiratie',    t('nav.inspiration')],
                ['/professioneel', t('nav.custom')],
                ['/contact',       t('nav.contact')],
              ].map(([to, label]) => (
                <li key={to}><Link to={to} className="hover:text-white transition-colors">{label}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase mb-4 text-[#c47a3a]">{t('footer.professionals')}</h4>
            <ul className="space-y-2.5 text-sm text-gray-400">
              <li><Link to="/architecten" className="hover:text-white transition-colors">{t('footer.techSheets')}</Link></li>
              <li><Link to="/custom" className="hover:text-white transition-colors">{t('footer.customStones')}</Link></li>
              <li><Link to="/offerte" className="hover:text-white transition-colors">{t('footer.b2bPricing')}</Link></li>
              {/* V6 punt 24: zelf-aanmelding voor handelaars/installateurs */}
              <li><Link to="/reseller-aanvraag" className="hover:text-white transition-colors">Vraag uw korting aan</Link></li>
            </ul>
            {/* Login / Account section */}
            <div className="mt-5 pt-4 border-t border-gray-800">
              {user ? (
                (() => {
                  const isAdmin = user.role === 'owner' || user.role === 'superadmin';
                  const isReseller = user.role === 'reseller';
                  const target = isAdmin ? '/admin' : isReseller ? '/reseller-dashboard' : '/dashboard';
                  return (
                    <Link to={target} className="inline-flex items-center gap-2 text-sm text-[#c47a3a] hover:text-white transition-colors">
                      {isAdmin ? <Settings size={14} /> : <User size={14} />}
                      {isAdmin ? t('nav.dashboard') : user.name}
                    </Link>
                  );
                })()
              ) : (
                <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#c47a3a] hover:text-white transition-colors">
                  <LogIn size={14} />
                  {t('footer.wholesaleLogin')}
                </Link>
              )}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold tracking-wider uppercase mb-4 text-[#c47a3a]">{t('footer.contact')}</h4>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-center gap-2.5"><Mail size={14} className="text-[#c47a3a] flex-shrink-0" /> {BRAND.email}</li>
              <li className="flex items-center gap-2.5"><Phone size={14} className="text-[#c47a3a] flex-shrink-0" /> {BRAND.phone}</li>
              <li className="flex items-start gap-2.5"><MapPin size={14} className="text-[#c47a3a] flex-shrink-0 mt-0.5" /> {t('footer.location')}</li>
              <li className="text-xs text-gray-500 pl-6">{t('footer.btw')}</li>
            </ul>
          </div>
        </div>
        <div className="mt-6 pt-5 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          <p>{t('footer.rights')}</p>
          <div className="flex gap-4">
            <Link to="/voorwaarden" className="hover:text-white transition-colors">{t('footer.terms')}</Link>
            <Link to="/retourbeleid" className="hover:text-white transition-colors">{t('footer.retour')}</Link>
            <Link to="/cookiebeleid" className="hover:text-white transition-colors">{t('footer.privacy')}</Link>
            <Link to="/cookiebeleid" className="hover:text-white transition-colors">{t('footer.cookies')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
